import { ActionWithPayload, RehydrateOptions } from "@obg/ngrx-utils";
import { fromSecondsToMilliseconds, updateState } from "@obg/ngx.global";

import { AppError } from "../app-error/app-error.model";

import { AUTH_ACTION_TYPE } from "./auth.action";
import { AUTH_INVALID_SESSION_ERROR_CODES, AUTH_MESSAGE_CODES, AUTH_SESSION_LIMIT_EXPIRED_CODES } from "./auth.const";
import { SessionStartPayload } from "./auth.model";
import { SESSION_ACTION_TYPE } from "./session.action";
import { SESSION_LOGIN_EXPIRED_CODES } from "./session.const";
import {
	CustomerDataNotConfirmedResponse,
	CustomerNotVerifiedData,
	LoginSuccessfulPayload,
	SessionError,
	SessionErrorStatus,
	SessionResponse,
	SessionState,
} from "./session.model";

const INITIAL_STATE: SessionState = {};

const SESSION_END_STATE = {
	isAuthenticated: false,
	isFirstLogin: undefined,
	token: undefined,
	sessionStartTimeInMillis: undefined,
	sessionLimitCheckPeriodInMillis: undefined,
	authThroughLogin: false,
} as SessionState;

const REHYDRATE_OPTIONS: RehydrateOptions<SessionState> = {
	name: "authReducer",
	excludeProps: [
		"errorCode",
		"isAuthenticated",
		"termsAndConditionsToken",
		"privacyPolicyToken",
		"consentsNotAcceptedToken",
		"customerNotVerifiedData",
		"latestEntityLegalNoticeToken",
		"authThroughLogin",
	],
};

// todo: create integration tests https://jirasson.betsson.local/browse/OBGB-5204
export function sessionReducer(state = INITIAL_STATE, action: ActionWithPayload): SessionState {
	switch (action.type) {
		case SESSION_ACTION_TYPE.login:
		case SESSION_ACTION_TYPE.loginWithPersonalNumber:
		case SESSION_ACTION_TYPE.loginWithToken:
		case SESSION_ACTION_TYPE.loginWithTwoFactorAuth:
		case SESSION_ACTION_TYPE.loginWithAuthenticationToken:
		case SESSION_ACTION_TYPE.logout:
		case SESSION_ACTION_TYPE.verify: {
			return updateState(state, {
				termsAndConditionsToken: undefined,
				termsAndConditionsResourceKeys: undefined,
				consentsNotAcceptedToken: undefined,
				customerNotVerifiedData: undefined,
				shouldRememberUser: action.payload?.shouldRememberUser,
				oneTimeLoginToken: undefined,
				authenticationToken: action.payload?.authenticationToken,
				profileNotCompletedData: undefined,
				redirectBrand: undefined,
				countryCode: undefined,
			});
		}
		case SESSION_ACTION_TYPE.loginBrandRedirection: {
			return updateState(state, {
				redirectBrand: true,
			});
		}
		case SESSION_ACTION_TYPE.loginCountryRestriction: {
			return updateState(state, {
				countryCode: action.payload,
			});
		}
		case SESSION_ACTION_TYPE.loginFail:
		case SESSION_ACTION_TYPE.loginWithVerificationCodeFail:
		case SESSION_ACTION_TYPE.loginWithPersonalNumberFail:
		case SESSION_ACTION_TYPE.loginWithTokenFail:
		case SESSION_ACTION_TYPE.loginWithAuthenticationTokenFail: {
			const payload = action.payload as SessionError;
			if (
				payload.code === AUTH_MESSAGE_CODES.login.termsAndConditionsNotAccepted &&
				payload.data.termsAndConditionsNotAcceptedLoginResponse
			) {
				return {
					...state,
					...SESSION_END_STATE,
					termsAndConditionsToken: payload.data.termsAndConditionsNotAcceptedLoginResponse
						.termsAndConditionsToken as string,
					oneTimeLoginToken: payload.data.termsAndConditionsNotAcceptedLoginResponse
						.oneTimeLoginToken as string,
				};
			}
			if (
				payload.code === AUTH_MESSAGE_CODES.login.latestTermsAndConditionsNotAccepted &&
				payload.data.latestTcNotAcceptedLoginResponse
			) {
				return {
					...state,
					...SESSION_END_STATE,
					termsAndConditionsToken: payload.data.latestTcNotAcceptedLoginResponse
						.termsAndConditionsToken as string,
					termsAndConditionsResourceKeys: payload.data.latestTcNotAcceptedLoginResponse
						.termsAndConditionsResourceKeys as string[],
					oneTimeLoginToken: payload.data.latestTcNotAcceptedLoginResponse.oneTimeLoginToken as string,
				};
			}
			if (payload.code === AUTH_MESSAGE_CODES.login.profileNotCompleted && payload.data) {
				const data = payload.data as CustomerDataNotConfirmedResponse;
				return {
					...state,
					...SESSION_END_STATE,
					authenticationToken: data.authenticationToken,
					profileNotCompletedData: {
						profileConfirmationToken: data.customerDataConfirmationToken,
						countryCode: data.customerCountry,
					},
				};
			}
			if (
				payload.code === AUTH_MESSAGE_CODES.login.latestPrivacyPolicyNotAccepted &&
				payload.data.latestPrivacyPolicyNotAcceptedLoginResponse
			) {
				return {
					...state,
					...SESSION_END_STATE,
					privacyPolicyToken: payload.data.latestPrivacyPolicyNotAcceptedLoginResponse
						.privacyPolicyToken as string,
				};
			}
			if (
				payload.code === AUTH_MESSAGE_CODES.login.latestEntityLegalNoticeNotAccepted &&
				payload.data.latestEntityLegalNoticeNotAcceptedLoginResponse
			) {
				let latestEntityLegalNoticeBodyKey!: string;

				if (payload.data.latestEntityLegalNoticeNotAcceptedLoginResponse.resourceKeys.length === 1) {
					latestEntityLegalNoticeBodyKey =
						payload.data.latestEntityLegalNoticeNotAcceptedLoginResponse.resourceKeys[0];
				}

				if (payload.data.latestEntityLegalNoticeNotAcceptedLoginResponse.resourceKeys.length > 1) {
					const descriptionRegexPattern = /description/;

					const index = payload.data.latestEntityLegalNoticeNotAcceptedLoginResponse.resourceKeys.findIndex(
						(element: string) => {
							return descriptionRegexPattern.test(element);
						}
					);

					latestEntityLegalNoticeBodyKey =
						payload.data.latestEntityLegalNoticeNotAcceptedLoginResponse.resourceKeys[index];
				}

				return {
					...state,
					...SESSION_END_STATE,
					latestEntityLegalNoticeBodyKey,
					latestEntityLegalNoticeToken: payload.data.latestEntityLegalNoticeNotAcceptedLoginResponse
						.latestEntityLegalNoticeToken as string,
				};
			}
			if (
				payload.code === AUTH_MESSAGE_CODES.login.consentsNotAccepted &&
				payload.data.consentsNotAcceptedLoginResponse
			) {
				return {
					...state,
					...SESSION_END_STATE,
					oneTimeLoginToken: payload.data.consentsNotAcceptedLoginResponse.oneTimeLoginToken as string,
					consentsNotAcceptedToken: payload.data.consentsNotAcceptedLoginResponse.token as string,
				};
			}
			if (
				payload.code === AUTH_MESSAGE_CODES.login.customerNotVerified &&
				payload.data.customerNotVerifiedLoginResponse
			) {
				return {
					...state,
					...SESSION_END_STATE,
					customerNotVerifiedData: payload.data.customerNotVerifiedLoginResponse as CustomerNotVerifiedData,
				};
			}
			if (
				(payload.code === AUTH_MESSAGE_CODES.login.twoFactorAuthenticationRequired ||
					payload.code === AUTH_MESSAGE_CODES.login.accountNotVerified ||
					payload.code === AUTH_MESSAGE_CODES.login.customerNotAllowed ||
					payload.code === AUTH_MESSAGE_CODES.login.invalidCredentials) &&
				(payload.data.remainingAttempts !== undefined ||
					payload.data?.challengeDetails?.remainingAttempts !== undefined)
			) {
				return {
					...state,
					...SESSION_END_STATE,
					remainingAttempts:
						payload.data?.remainingAttempts ?? payload.data?.challengeDetails.remainingAttempts,
					loginToken: !!payload.data?.loginToken && payload.data?.loginToken,
					customerId: !!payload.data?.customerId && payload.data?.customerId,
				};
			}
			if (SESSION_LOGIN_EXPIRED_CODES.includes(payload.code)) {
				return {
					...state,
					...SESSION_END_STATE,
					customerCanLogInAt: payload.customerCanLogInAt,
				};
			}

			return updateState(state, SESSION_END_STATE);
		}
		case SESSION_ACTION_TYPE.updateCustomerNotVerifiedData: {
			return {
				...state,
				customerNotVerifiedData: action.payload,
			};
		}
		case SESSION_ACTION_TYPE.clearCustomerNotVerifiedData: {
			return {
				...state,
				customerNotVerifiedData: undefined,
			};
		}
		case SESSION_ACTION_TYPE.loginSuccess:
		case SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess:
		case SESSION_ACTION_TYPE.loginWithTokenSuccess:
		case SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess:
		case SESSION_ACTION_TYPE.loginWithAuthenticationTokenSuccess: {
			const response: LoginSuccessfulPayload = action.payload;

			const isNotLoginWithToken = action.type !== SESSION_ACTION_TYPE.loginWithTokenSuccess;

			const lastUsername = keepUsernameAlive(state, response);
			return updateState(state, {
				isAuthenticated: true,
				token: response.sessionToken,
				sessionErrorStatus: undefined,
				customerNotVerifiedData: undefined,
				isFirstLogin: response.isFirstLogin,
				...(isNotLoginWithToken && { lastActiveUsername: lastUsername }),
				isLastActiveUser: true,
				// todo: Use the sessionStartTime from the API in the LoginSuccessfulResponse instead of setting it like that
				// jira: https://jirasson.betsson.local/browse/OCA-1201
				sessionStartTimeInMillis: Date.now(),
				sessionLimitCheckPeriodInMillis: fromSecondsToMilliseconds(
					response.personalSessionLimitCheckPeriodSeconds
				),
				remainingAttempts: 10, // TODO: AOCC-11488 this needs to be removed once we have the initial value.
				authThroughLogin: true,
				authenticationToken: undefined,
			});
		}
		case AUTH_ACTION_TYPE.sessionStart: {
			if (state.isAuthenticated) {
				return state;
			}
			const payload = action.payload as SessionStartPayload;

			return updateState(state, {
				isAuthenticated: true,
				token: payload.token,
				sessionErrorStatus: undefined,
				customerNotVerifiedData: undefined,
				isFirstLogin: payload.isFirstLogin,
				lastActiveUsername: undefined,
				sessionStartTimeInMillis: Date.now(),
				sessionLimitCheckPeriodInMillis: payload.sessionLimitCheckPeriodInMillis,
			});
		}
		case SESSION_ACTION_TYPE.keepAliveSuccess: {
			return updateState(state, {
				sessionErrorStatus: undefined,
			});
		}
		case SESSION_ACTION_TYPE.verifySuccess: {
			const response: SessionResponse = action.payload;
			return updateState(state, {
				isAuthenticated: true,
				token: response.sessionToken,
				sessionErrorStatus: undefined,
				// this is needed for login from native and we need to respect state value also for refresh
				sessionStartTimeInMillis: state.sessionStartTimeInMillis || Date.now(),
			});
		}
		case SESSION_ACTION_TYPE.logoutSuccess:
		case SESSION_ACTION_TYPE.logoutFail:
		case SESSION_ACTION_TYPE.verifyFail: {
			return updateState(state, {
				...SESSION_END_STATE,
				nextTimeLogin: undefined,
			});
		}
		case AUTH_ACTION_TYPE.sessionEnd: {
			if (state.isAuthenticated === false) {
				return state;
			}

			return updateState(state, SESSION_END_STATE);
		}
		case SESSION_ACTION_TYPE.keepAlive:
		case SESSION_ACTION_TYPE.checkSessionExpired: {
			return updateState(state, {
				sessionErrorStatus: SessionErrorStatus.ExpiryCheckInProgress,
			});
		}
		case SESSION_ACTION_TYPE.sessionExpired: {
			const payload = action.payload as AppError;
			if (AUTH_SESSION_LIMIT_EXPIRED_CODES.includes(payload.code)) {
				return updateState(state, {
					...SESSION_END_STATE,
					sessionErrorStatus: SessionErrorStatus.SessionLimitExpired,
				});
			}
			return updateState(state, {
				...SESSION_END_STATE,
				sessionErrorStatus: SessionErrorStatus.Expired,
			});
		}
		case SESSION_ACTION_TYPE.sessionInvalid: {
			const payload = action.payload as AppError;
			if (payload.code === AUTH_INVALID_SESSION_ERROR_CODES.sessionLimitExpired) {
				return updateState(state, {
					...SESSION_END_STATE,
					sessionErrorStatus: SessionErrorStatus.SessionLimitExpired,
				});
			}
			return updateState(state, {
				...SESSION_END_STATE,
				sessionErrorStatus: SessionErrorStatus.Invalid,
				sessionErrorStatusMessage: payload.code,
			});
		}
		case SESSION_ACTION_TYPE.resetSession: {
			return updateState(INITIAL_STATE, {
				lastActiveUsername: state.lastActiveUsername,
				isLastActiveUser: true,
				isAuthenticated: false,
			});
		}
		case SESSION_ACTION_TYPE.clearTermsAndConditionsToken: {
			return updateState(state, {
				termsAndConditionsToken: undefined,
			});
		}
		case SESSION_ACTION_TYPE.getNextTimeLoginSuccess: {
			return updateState(state, {
				nextTimeLogin: action.payload,
			});
		}
		case SESSION_ACTION_TYPE.authenticationTransaction:
		case SESSION_ACTION_TYPE.loginWithAuthenticationTokenSave: {
			return updateState(state, {
				authenticationToken: action.payload,
			});
		}
		default:
			return state;
	}
}

function keepUsernameAlive(state: SessionState, response: LoginSuccessfulPayload): string | undefined {
	const shouldRememberUser = state.shouldRememberUser;
	const lastActiveUsername = state.lastActiveUsername;
	const btoaResponseUsername = btoa(response.username ?? "");

	if (shouldRememberUser && btoaResponseUsername) {
		return btoaResponseUsername;
	}

	if (shouldRememberUser && lastActiveUsername) {
		return lastActiveUsername;
	}

	return undefined;
}

sessionReducer.prototype.rehydrateOptions = REHYDRATE_OPTIONS;
